import { useMemo } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import { buildLast30DaysPeriod, last30DaysQueryVariables } from "../period";
import { mapRosterEventsToMovementRows } from "../mapRosterEventToMovementRow";
import { ROSTER_MOVEMENT_KINDS } from "../movementBands";
import { groupMovementRowsByBand, type MovementBandGroups } from "../movementPreview";
import { SECTION_ROSTER_ACTIVITY_QUERY } from "../queries";
import type { Last30DaysPeriod } from "../period";

export function useSectionRosterActivity(sectionId: string | null): {
  bands: MovementBandGroups;
  loading: boolean;
  refetching: boolean;
  error?: ApolloError;
  refetch: () => void;
  period: Last30DaysPeriod;
  sectionName: string | null;
} {
  const period = useMemo(() => buildLast30DaysPeriod(), []);
  const variables = useMemo(
    () => ({
      sectionId: sectionId ?? "",
      ...last30DaysQueryVariables(period),
      kinds: [...ROSTER_MOVEMENT_KINDS]
    }),
    [period, sectionId]
  );

  const { data, previousData, loading, error, refetch } = useQuery(SECTION_ROSTER_ACTIVITY_QUERY, {
    variables,
    skip: !sectionId,
    notifyOnNetworkStatusChange: true
  });

  const resolvedData = data ?? (previousData?.section?.id === sectionId && sectionId ? previousData : undefined);
  const bands = useMemo(
    () => groupMovementRowsByBand(mapRosterEventsToMovementRows(resolvedData?.section?.rosterActivity ?? [])),
    [resolvedData]
  );

  return {
    bands,
    loading: Boolean(sectionId) && loading && !resolvedData,
    refetching: loading && !!resolvedData,
    error,
    refetch,
    period,
    sectionName: resolvedData?.section?.name ?? null
  };
}
